import { supabase } from '@/lib/supabase'
import type {
  TeamMember,
  TeamInvitation,
  OrgRole,
  CreateTeamMemberFormData,
  CreateTeamMemberResponse,
} from '@/types/team-members'

/**
 * Fetch all team members for an organization
 * Uses get_organization_members RPC function with pagination support
 */
export async function fetchTeamMembers(
  organizationId: string,
  limit: number = 50,
  offset: number = 0
): Promise<TeamMember[]> {
  const { data, error } = await supabase.rpc('get_organization_members', {
    organization_id: organizationId,
    results_limit: limit,
    results_offset: offset,
  })

  if (error) throw error
  if (!data || data.length === 0) return []

  // Fetch user profiles for proper display names
  const userIds = (data as any[]).map((m: any) => m.user_id)
  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('user_id, display_name')
    .in('user_id', userIds)

  const profileMap = new Map(
    (profiles || []).map((p) => [p.user_id, p.display_name])
  )

  // Transform response with proper display names from user_profiles
  return (data as any[]).map((member: any) => ({
    user_id: member.user_id,
    role: member.role,
    is_primary_owner: member.is_primary_owner,
    email: member.email,
    display_name: profileMap.get(member.user_id) || member.email || null,
    created_at: member.created_at,
  }))
}

/**
 * Fetch pending invitations for an organization
 */
export async function fetchInvitations(
  organizationId: string
): Promise<TeamInvitation[]> {
  const { data, error } = await supabase.rpc('get_organization_invitations', {
    p_organization_id: organizationId,
  })

  if (error) throw error

  // Transform the response to match TeamInvitation interface
  return (data as any[])?.map((inv: any) => ({
    id: inv.id,
    organization_id: inv.organization_id,
    email: inv.email,
    role: inv.role as OrgRole,
    created_at: inv.created_at,
    created_by: inv.created_by,
  })) || []
}

/**
 * Invite a user to the organization
 */
export async function inviteMember(
  organizationId: string,
  email: string,
  role: OrgRole
): Promise<TeamInvitation> {
  const { data, error } = await supabase.rpc('invite_user_to_organization', {
    organization_id: organizationId,
    email,
    role,
  })

  if (error) throw error

  // The RPC function returns the invitation data
  return data as TeamInvitation
}

/**
 * Update a team member's role
 */
export async function updateMemberRole(
  organizationId: string,
  userId: string,
  newRole: OrgRole
): Promise<void> {
  const { error } = await supabase.rpc('update_organization_member_role', {
    organization_id: organizationId,
    user_id: userId,
    new_role: newRole,
  })

  if (error) throw error
}

/**
 * Remove a team member from the organization
 */
export async function removeMember(
  organizationId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase.rpc('remove_organization_member', {
    organization_id: organizationId,
    user_id: userId,
  })

  if (error) throw error
}

/**
 * Cancel a pending invitation
 */
export async function cancelInvitation(invitationId: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_invitation', {
    invitation_id: invitationId,
  })

  if (error) throw error
}

/**
 * Create a new team member directly (via Edge Function)
 * Creates user account, adds to org, and assigns locations in one operation
 */
export async function createTeamMember(
  organizationId: string,
  data: CreateTeamMemberFormData
): Promise<CreateTeamMemberResponse> {
  const { data: response, error } = await supabase.functions.invoke('create-team-member', {
    body: {
      email: data.email,
      display_name: data.display_name,
      organization_id: organizationId,
      role: data.role,
      location_assignments: data.location_assignments,
    },
  })

  if (error) throw error
  if (!response.success) throw new Error(response.error || 'Failed to create team member')

  return response as CreateTeamMemberResponse
}
